import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FlatButton } from '@framework/ui/atoms';
import { classNames } from '@framework/utils';
import { FirebaseAuthenticationClient } from '@framework/firebase/auth';
import { trpcClient } from '@framework/trpcClient';

type Props = {
  temporaryId: string;
  setTemporaryId: (temporaryId: string) => void;
};

export const MultiFactorAuthForm: React.FC<Props> = ({ temporaryId, setTemporaryId }: Props) => {
  const { t } = useTranslation();
  const [oneTimePassword, setOneTimePassword] = useState('');
  const [oneTimePasswordError, setOneTimePasswordError] = useState('');
  const [multiFactorAuthProcessing, setMultiFactorAuthProcessing] = useState(false);
  const isOneTimePasswordSendable = oneTimePassword && oneTimePasswordError === '';

  const handleChangeOneTimePassword = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const email = event.target.value;
    setOneTimePassword(email);
  }, []);

  const handleBlurOneTimePassword = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      if (event.target.value === '') {
        setOneTimePasswordError(t('auth.mfa.codeRequired'));
      } else {
        setOneTimePasswordError('');
      }
    },
    [t]
  );

  const handleOneTimePasswordSubmit = useCallback(async () => {
    if (temporaryId == '' || oneTimePassword == '') {
      return;
    }
    setMultiFactorAuthProcessing(true);
    setOneTimePasswordError('');

    try {
      const result = await trpcClient.user.auth.oneTimePasswordVerification.mutate({ oneTimePassword, temporaryId });
      if (result.ok) {
        await FirebaseAuthenticationClient.signInWithCustomToken(result.token);
      } else {
        setOneTimePasswordError(t('auth.mfa.loginFailed'));
      }
    } catch (error) {
      console.error(error);
      setOneTimePasswordError(t('auth.mfa.systemError'));
    } finally {
      setMultiFactorAuthProcessing(false);
    }
  }, [temporaryId, oneTimePassword, t]);

  const handleBackToLoginWithEmailForm = useCallback(() => {
    setTemporaryId('');
    setOneTimePassword('');
    setOneTimePasswordError('');
    setMultiFactorAuthProcessing(false);
  }, [setTemporaryId]);

  return (
    <>
      <div className="flex w-80 flex-col px-5 py-2">
        <div className="cursor-pointer font-bold text-brand hover:underline" onClick={handleBackToLoginWithEmailForm}>
          {t('auth.back.label')}
        </div>
        <div className="pb-2 pt-4 font-bold">{t('auth.mfa.codeLabel')}</div>
        <input
          type="text"
          value={oneTimePassword}
          onChange={handleChangeOneTimePassword}
          onBlur={handleBlurOneTimePassword}
          className={classNames('w-full rounded-sm border p-2', oneTimePasswordError ? 'border-red-700' : '')}
          autoFocus
          disabled={multiFactorAuthProcessing}
        />
        {oneTimePasswordError && <div className="text-sm text-red-700">{oneTimePasswordError}</div>}
        <div className="pt-4" />
        <FlatButton
          color="brand"
          onClick={handleOneTimePasswordSubmit}
          disabled={!isOneTimePasswordSendable || multiFactorAuthProcessing}
          loading={multiFactorAuthProcessing}
        >
          {t('auth.mfa.submit')}
        </FlatButton>
      </div>
      <div className="mt-8">
        {t('auth.mfa.codeSent')}
        <br />
        <br />
        {t('auth.mfa.codeExpiredHint')}
        <br />
        <button onClick={handleBackToLoginWithEmailForm} className="text-blue-700 hover:underline focus:outline-none">
          {t('auth.mfa.hereLink')}
        </button>
        {t('auth.mfa.retryHint')}
      </div>
    </>
  );
};
